/**
 * Country-specific fiscal type configuration.
 *
 * Generic codes stored in DB: "individual" | "freelancer" | "small_company" | "corporation"
 * Each country maps these to its local legal forms.
 *
 * Paying entity: Appy Solutions Ltd, Sofia, Bulgaria (all payouts in EUR).
 */

export type FiscalTypeCode = "individual" | "freelancer" | "small_company" | "corporation";

export interface FiscalTypeConfig {
  code: FiscalTypeCode;
  requiresBusinessName: boolean;
  requiresVatNumber: boolean;
  requiresRegistrationNumber: boolean;
  selfBillingEligible: boolean;
}

/**
 * Withholding tax rules per country for individuals.
 *
 * BG domestic: 10% advance income tax (авансов данък) under ЗДДФЛ Art. 43 al. 4.
 * All other EU countries: 0% — DTT Art. 21 ("Other Income") gives exclusive taxing
 * rights to the country of residence, so Bulgaria cannot withhold.
 */
export interface WithholdingTaxConfig {
  /** Withholding tax percentage for individuals (PF) */
  individualPct: number;
  /** Whether Appy Solutions must withhold and report */
  withholdingRequired: boolean;
  /** BG tax filing reference (Декларация по чл. 55 — quarterly) */
  filingReference?: string;
}

export const WITHHOLDING_TAX: Record<string, WithholdingTaxConfig> = {
  bg: { individualPct: 10, withholdingRequired: true, filingReference: "Декл. чл. 55 ЗДДФЛ" },
  // All other EU countries: 0% under DTT
  ro: { individualPct: 0, withholdingRequired: false },
  de: { individualPct: 0, withholdingRequired: false },
  fr: { individualPct: 0, withholdingRequired: false },
  hu: { individualPct: 0, withholdingRequired: false },
  it: { individualPct: 0, withholdingRequired: false },
  el: { individualPct: 0, withholdingRequired: false },
  at: { individualPct: 0, withholdingRequired: false },
  ch: { individualPct: 0, withholdingRequired: false },
  nl: { individualPct: 0, withholdingRequired: false },
  be: { individualPct: 0, withholdingRequired: false },
  pl: { individualPct: 0, withholdingRequired: false },
  es: { individualPct: 0, withholdingRequired: false },
  hr: { individualPct: 0, withholdingRequired: false },
  rs: { individualPct: 0, withholdingRequired: false },
  ie: { individualPct: 0, withholdingRequired: false },
};

/**
 * Get withholding tax config for a country.
 * Returns 0% for unknown countries (safe default — no withholding).
 */
export function getWithholdingTax(
  countryCode: string,
  fiscalType: FiscalTypeCode,
): { pct: number; required: boolean } {
  // Withholding only applies to individuals — businesses invoice and handle their own tax
  if (fiscalType !== "individual") {
    return { pct: 0, required: false };
  }
  const config = WITHHOLDING_TAX[countryCode.toLowerCase()];
  if (!config) return { pct: 0, required: false };
  return { pct: config.individualPct, required: config.withholdingRequired };
}

export const FISCAL_TYPES: FiscalTypeConfig[] = [
  {
    code: "individual",
    requiresBusinessName: false,
    requiresVatNumber: false,
    requiresRegistrationNumber: false,
    selfBillingEligible: true,
  },
  {
    code: "freelancer",
    requiresBusinessName: true,
    requiresVatNumber: false,
    requiresRegistrationNumber: true,
    selfBillingEligible: false,
  },
  {
    code: "small_company",
    requiresBusinessName: true,
    requiresVatNumber: true,
    requiresRegistrationNumber: true,
    selfBillingEligible: false,
  },
  {
    code: "corporation",
    requiresBusinessName: true,
    requiresVatNumber: true,
    requiresRegistrationNumber: true,
    selfBillingEligible: false,
  },
];

/**
 * Country-specific local names for each fiscal type.
 * Used to display the familiar legal form name for each country.
 * i18n keys: account.wallet.fiscalTypeLocal_{code}
 *
 * These are the values shown in parentheses next to the generic label,
 * e.g. "Freelancer (PFA)" for RO, "Freelancer (Freiberufler)" for DE.
 */
export const COUNTRY_FISCAL_LABELS: Record<string, Record<FiscalTypeCode, string>> = {
  ro: {
    individual: "Persoană fizică",
    freelancer: "PFA",
    small_company: "SRL",
    corporation: "SA",
  },
  bg: {
    individual: "Физическо лице",
    freelancer: "Свободна професия / ЕТ",
    small_company: "ЕООД / ООД",
    corporation: "АД",
  },
  de: {
    individual: "Privatperson",
    freelancer: "Freiberufler",
    small_company: "GmbH / UG",
    corporation: "AG",
  },
  fr: {
    individual: "Particulier",
    freelancer: "Auto-entrepreneur",
    small_company: "SARL / SAS",
    corporation: "SA",
  },
  hu: {
    individual: "Magánszemély",
    freelancer: "Egyéni vállalkozó",
    small_company: "Kft.",
    corporation: "Nyrt.",
  },
  it: {
    individual: "Persona fisica",
    freelancer: "Libero professionista",
    small_company: "S.r.l.",
    corporation: "S.p.A.",
  },
  el: {
    individual: "Ιδιώτης",
    freelancer: "Ελεύθερος επαγγελματίας",
    small_company: "ΙΚΕ / ΕΠΕ",
    corporation: "ΑΕ",
  },
  // Countries that share labels with another country
  at: {
    individual: "Privatperson",
    freelancer: "Freiberufler",
    small_company: "GmbH",
    corporation: "AG",
  },
  ch: {
    individual: "Privatperson",
    freelancer: "Freiberufler",
    small_company: "GmbH",
    corporation: "AG",
  },
  nl: {
    individual: "Particulier",
    freelancer: "ZZP'er / Eenmanszaak",
    small_company: "B.V.",
    corporation: "N.V.",
  },
  be: {
    individual: "Particulier",
    freelancer: "Indépendant",
    small_company: "SRL / BV",
    corporation: "SA / NV",
  },
  pl: {
    individual: "Osoba fizyczna",
    freelancer: "Jednoosobowa działalność",
    small_company: "Sp. z o.o.",
    corporation: "S.A.",
  },
  es: {
    individual: "Persona física",
    freelancer: "Autónomo",
    small_company: "S.L.",
    corporation: "S.A.",
  },
  hr: {
    individual: "Fizička osoba",
    freelancer: "Slobodna djelatnost",
    small_company: "d.o.o.",
    corporation: "d.d.",
  },
  rs: {
    individual: "Физичко лице",
    freelancer: "Предузетник",
    small_company: "д.о.о.",
    corporation: "а.д.",
  },
  ie: {
    individual: "Individual",
    freelancer: "Sole Trader",
    small_company: "Ltd.",
    corporation: "PLC",
  },
};

/**
 * Get fiscal type configs for a given country.
 * Returns the 4 generic types with country-specific local labels.
 */
export function getFiscalTypesForCountry(countryCode: string) {
  const labels = COUNTRY_FISCAL_LABELS[countryCode.toLowerCase()] ?? COUNTRY_FISCAL_LABELS["ro"]; // fallback to RO

  return FISCAL_TYPES.map((ft) => ({
    ...ft,
    localLabel: labels[ft.code],
  }));
}

/**
 * Get the local label for a specific fiscal type in a country.
 */
export function getFiscalLocalLabel(countryCode: string, code: FiscalTypeCode): string {
  const labels = COUNTRY_FISCAL_LABELS[countryCode.toLowerCase()];
  return labels?.[code] ?? code;
}
